import React, {
  useContext, useEffect, useState, useCallback,
} from 'react';
import styled from 'styled-components';
import * as Portal from '@radix-ui/react-portal';
import { AnimatePresence, motion } from 'framer-motion';
import { asRem, useBreakpoint } from 'lib/css';
import { getAccountKey } from 'core/localStore';
import { Config } from 'config';
import { Display20Bold, Text14Regular } from 'style/typography';
import { UserContext } from 'core/UserContext';
import { AvailableSports } from 'modal';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pages } from 'core/pages';
import { dataFromPath } from 'lib/utils';
import { TourContext } from './TourContext';
import { tourSteps } from './tourSteps';
import { tournamentTourSteps } from './tournamentTourSteps';
import { TourTooltip } from './TourTooltip';

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 12;

const tourStepSets = {
  main: tourSteps,
  tournament: tournamentTourSteps,
};

const OverlayRoot = styled(Portal.Root)`
  position: fixed;
  inset: 0;
  z-index: 10040;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10040;
  background: ${({ $dimmed }) => ($dimmed ? 'rgb(13 17 22 / 75%)' : 'transparent')};
`;

const Spotlight = styled.div`
  position: fixed;
  z-index: 10041;
  border-radius: ${asRem(12)};
  box-shadow: 0 0 0 9999px rgb(13 17 22 / 75%);
  pointer-events: none;
  transition: top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease;
`;

const TooltipContainer = styled(motion.div)`
  position: fixed;
  z-index: 10042;
`;

const CenteredWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10042;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const CenteredCard = styled(motion.div)`
  pointer-events: auto;
  background: var(--color-secondary-bg);
  border: 1px solid var(--color-border-2);
  border-radius: ${asRem(20)};
  padding: ${asRem(32)};
  max-width: ${asRem(440)};
  min-width: ${asRem(320)};
  text-align: center;
  box-shadow: 0 ${asRem(8)} ${asRem(32)} rgba(0, 0, 0, 0.4);

  .centered-title {
    margin-bottom: ${asRem(12)};
  }

  .centered-body {
    color: var(--color-text-2);
    margin-bottom: ${asRem(24)};
  }

  .centered-actions {
    display: flex;
    justify-content: center;
    gap: ${asRem(12)};
    flex-wrap: wrap;

    .btn-skip {
      background: transparent;
      border: 1px solid var(--color-border-2);
      color: var(--color-text-1);
      padding: ${asRem(10)} ${asRem(20)};
      border-radius: ${asRem(40)};
      cursor: pointer;
      font-size: var(--text-md-fontSize);

      &:hover {
        border-color: var(--color-button-primary-bg);
      }
    }

    .btn-start {
      background: var(--color-button-primary-bg);
      color: var(--color-text-rev);
      border: none;
      padding: ${asRem(10)} ${asRem(20)};
      border-radius: ${asRem(40)};
      cursor: pointer;
      font-weight: var(--bold-fontWeight);
      font-size: var(--text-md-fontSize);
    }

    .btn-action {
      background: transparent;
      border: 1px solid var(--color-border-2);
      color: var(--color-text-1);
      padding: ${asRem(10)} ${asRem(20)};
      border-radius: ${asRem(40)};
      cursor: pointer;
      font-size: var(--text-md-fontSize);

      &:hover {
        border-color: var(--color-button-primary-bg);
        color: var(--color-button-primary-bg);
      }
    }
  }
`;

const TOOLTIP_MAX_WIDTH = 380;
const VIEWPORT_PADDING = 16;

function clampHorizontal(tooltipLeft) {
  const maxLeft = window.innerWidth - TOOLTIP_MAX_WIDTH - VIEWPORT_PADDING;
  return Math.max(VIEWPORT_PADDING, Math.min(tooltipLeft, maxLeft));
}

function getTooltipPosition(targetRect, placement) {
  if (!targetRect) return { top: 0, left: 0 };

  const {
    top, left, width, height,
  } = targetRect;

  let tooltipTop;
  let tooltipLeft;

  switch (placement) {
    case 'bottom':
      tooltipTop = top + height + SPOTLIGHT_PADDING + TOOLTIP_GAP;
      tooltipLeft = left + width / 2 - TOOLTIP_MAX_WIDTH / 2;
      break;
    case 'bottom-end':
      tooltipTop = top + height + SPOTLIGHT_PADDING + TOOLTIP_GAP;
      tooltipLeft = left + width - TOOLTIP_MAX_WIDTH;
      break;
    case 'top':
      tooltipTop = top - SPOTLIGHT_PADDING - TOOLTIP_GAP;
      tooltipLeft = left + width / 2 - TOOLTIP_MAX_WIDTH / 2;
      break;
    default:
      tooltipTop = top + height + SPOTLIGHT_PADDING + TOOLTIP_GAP;
      tooltipLeft = left + width / 2 - TOOLTIP_MAX_WIDTH / 2;
      break;
  }

  return {
    top: tooltipTop,
    left: clampHorizontal(tooltipLeft),
  };
}

export function TourOverlay() {
  const userContext = useContext(UserContext);
  const {
    isTourRunning, tourStepIndex, activeTourId,
    nextStep, prevStep, stopTour,
  } = useContext(TourContext);
  const isDesktop = useBreakpoint('sm');
  const [targetRect, setTargetRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const steps = tourStepSets[activeTourId] || tourSteps;
  const currentStep = steps[tourStepIndex];
  const isLastStep = tourStepIndex === steps.length - 1;
  const isCentered = !currentStep?.target;
  const hasCustomActions = currentStep?.actions?.length > 0;

  const updateTargetRect = useCallback(() => {
    if (!currentStep?.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(currentStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
      });
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  useEffect(() => {
    if (!isTourRunning) return undefined;

    updateTargetRect();

    const handleResize = () => updateTargetRect();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isTourRunning, tourStepIndex, updateTargetRect]);

  useEffect(() => {
    if (!isTourRunning) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        stopTour(true);
      } else if (e.key === 'ArrowRight') {
        nextStep(steps.length);
      } else if (e.key === 'ArrowLeft') {
        prevStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTourRunning, nextStep, prevStep, stopTour, steps.length]);

  const handleActionClick = useCallback((action) => {
    stopTour(true);
    if (action.page) {
      const match = location.pathname.match(/\/sports\/tournament\/([^/]+)/);
      const tournamentKey = match?.[1];
      if (tournamentKey) {
        const page = dataFromPath(Pages, `sports.cricket.tournament.${action.page}`);
        if (page?.meta?.urlFor) {
          navigate(page.meta.urlFor({ key: decodeURIComponent(tournamentKey) }));
        }
      }
    }
  }, [stopTour, navigate, location.pathname]);

  if (!isTourRunning) return null;
  if (!isDesktop) return null;
  if (userContext.sport !== AvailableSports.Cricket.key) return null;
  if (getAccountKey() === Config.rzAccountKey) return null;

  const tooltipPos = targetRect
    ? getTooltipPosition(targetRect, currentStep.placement)
    : null;

  return (
    <OverlayRoot>
      <Backdrop $dimmed={isCentered} onClick={() => stopTour(true)} />

      {targetRect && !isCentered && (
        <Spotlight
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}

      {isCentered && (
        <CenteredWrapper>
          <AnimatePresence mode="wait">
            <CenteredCard
              key={`centered-${tourStepIndex}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <Display20Bold className="centered-title">
                {currentStep.title}
              </Display20Bold>
              <Text14Regular className="centered-body">
                {currentStep.content}
              </Text14Regular>
              <div className="centered-actions">
                {isLastStep && hasCustomActions ? (
                  <>
                    {currentStep.actions.map((action) => (
                      <button
                        key={action.label}
                        type="button"
                        className="btn-action"
                        onClick={() => handleActionClick(action)}
                      >
                        {action.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn-start"
                      onClick={() => stopTour(true)}
                    >
                      Done
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn-skip"
                      onClick={() => stopTour(true)}
                    >
                      Skip Tour
                    </button>
                    <button
                      type="button"
                      className="btn-start"
                      onClick={() => nextStep(steps.length)}
                    >
                      Start Tour
                    </button>
                  </>
                )}
              </div>
            </CenteredCard>
          </AnimatePresence>
        </CenteredWrapper>
      )}

      {!isCentered && tooltipPos && (
        <AnimatePresence mode="wait">
          <TooltipContainer
            key={`tooltip-${tourStepIndex}`}
            style={{
              top: tooltipPos.top,
              left: tooltipPos.left,
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
          >
            <TourTooltip
              step={currentStep}
              stepIndex={tourStepIndex}
              totalSteps={steps.length}
              isLastStep={isLastStep}
              onNext={() => nextStep(steps.length)}
              onPrev={prevStep}
              onSkip={() => stopTour(true)}
            />
          </TooltipContainer>
        </AnimatePresence>
      )}
    </OverlayRoot>
  );
}
